import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { procurementApi } from 'api/procurement';

const PO_PREVENT_VENDOR_SEND = 200_000;

export function usePurchaseOrderActions() {
    const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
    const [isSubmittingCancel, setIsSubmittingCancel] = useState(false);
    const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);

    const openCancelDialog = useCallback((purchaseOrder) => {
        setSelectedPurchaseOrder(purchaseOrder ?? null);
        setIsCancelDialogOpen(true);
    }, []);

    const closeCancelDialog = useCallback(() => {
        setIsCancelDialogOpen(false);
        setSelectedPurchaseOrder(null);
    }, []);

    const confirmCancel = useCallback(
        async (reasonCode) => {
            if (!selectedPurchaseOrder?.id) {
                return { status: false, msg: 'Missing purchase order ID.' };
            }
            if (!reasonCode) {
                return { status: false, msg: 'Reason code is required.' };
            }
            if (isSubmittingCancel) {
                return { status: false, msg: 'Cancellation already in progress.' };
            }

            setIsSubmittingCancel(true);
            try {
                return await procurementApi.cancelPurchaseOrder(selectedPurchaseOrder.id, reasonCode);
            } finally {
                setIsSubmittingCancel(false);
            }
        },
        [isSubmittingCancel, selectedPurchaseOrder],
    );

    const downloadCsv = useCallback(async (purchaseOrder) => {
        if (!purchaseOrder?.id) return;
        const response = await procurementApi.downloadPOCSVWithStatus(purchaseOrder.id, purchaseOrder.vendorName);
        if (response?.status === false) {
            toast.error(response?.msg ?? 'Unable to download CSV.');
        }
    }, []);

    const downloadPdf = useCallback(async (purchaseOrder) => {
        if (!purchaseOrder?.id) return;
        const response = await procurementApi.downloadPOPDFWithStatus(
            purchaseOrder.id,
            purchaseOrder.vendorName,
            purchaseOrder.facilityName,
        );
        if (response?.status === false) {
            toast.error(response?.msg ?? 'Unable to download PDF.');
        }
    }, []);

    const downloadShipList = useCallback(async (purchaseOrder) => {
        if (!purchaseOrder?.id) return;
        const response = await procurementApi.downloadPOShipListWithStatus(purchaseOrder.id);
        if (response?.status === false) {
            toast.error(response?.msg ?? 'Unable to download ship list.');
        }
    }, []);

    const sendToVendor = useCallback(async (purchaseOrder, deptLeadCheck) => {
        if (!purchaseOrder?.id) {
            return { status: false, msg: 'Missing purchase order ID.' };
        }

        if (!deptLeadCheck && purchaseOrder?.poTotalCost >= PO_PREVENT_VENDOR_SEND) {
            return {
                status: false,
                msg: 'Only department leads can send POs at this total cost.',
            };
        }

        return procurementApi.sendPurchaseOrderToVendor(purchaseOrder.id);
    }, []);

    const canSendToVendor = useCallback((purchaseOrder, deptLeadCheck) => {
        if (!purchaseOrder?.id) return false;
        if (deptLeadCheck) return true;
        return (purchaseOrder?.poTotalCost ?? 0) < PO_PREVENT_VENDOR_SEND;
    }, []);

    return {
        cancelDialog: {
            close: closeCancelDialog,
            isOpen: isCancelDialogOpen,
            open: openCancelDialog,
        },
        cancelState: {
            isSubmitting: isSubmittingCancel,
            selectedPurchaseOrder,
        },
        confirmCancel,
        downloadCsv,
        downloadPdf,
        downloadShipList,
        sendToVendor,
        canSendToVendor,
    };
}

usePurchaseOrderActions.displayName = 'usePurchaseOrderActions';
